/**
 * 资产照片上传前处理：压缩手机原图，便于直接上传。
 * 目标边长 ≤ 1920，体积尽量 ≤ 2.5MB；后端硬上限 12MB。
 */

export const ASSET_PHOTO_MAX_COUNT = 5;
/** 后端允许的上传硬上限 */
export const ASSET_PHOTO_MAX_BYTES = 12 * 1024 * 1024;
/** 压缩目标体积 */
export const ASSET_PHOTO_TARGET_BYTES = Math.floor(2.5 * 1024 * 1024);
export const ASSET_PHOTO_MAX_EDGE = 1920;

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('图片无法预览，请换用 JPG/PNG 格式'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

/**
 * 将图片压缩为 JPEG（保留透明时用 PNG 体积可能更大，统一转 JPEG 更利于手机直传）。
 */
export async function compressImageFile(file, options = {}) {
  const maxEdge = options.maxEdge || ASSET_PHOTO_MAX_EDGE;
  const targetBytes = options.targetBytes || ASSET_PHOTO_TARGET_BYTES;
  const img = await loadImageFromFile(file);
  const scale = Math.min(1, maxEdge / Math.max(img.width || 1, img.height || 1));
  const w = Math.max(1, Math.round((img.width || 1) * scale));
  const h = Math.max(1, Math.round((img.height || 1) * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, 0, 0, w, h);

  let quality = 0.85;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  while (blob && blob.size > targetBytes && quality > 0.45) {
    quality -= 0.1;
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }
  if (!blob) {
    throw new Error('图片压缩失败');
  }
  const base = String(file.name || 'asset-photo').replace(/\.[^.]+$/, '') || 'asset-photo';
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

export async function prepareAssetPhotoFile(file) {
  if (!file) {
    throw new Error('未选择图片');
  }
  const type = String(file.type || '').toLowerCase();
  if (type && !(type.startsWith('image/') || type === 'application/octet-stream')) {
    throw new Error('仅支持图片文件');
  }
  if (/heic|heif/.test(type) || /\.(heic|heif)$/i.test(file.name || '')) {
    throw new Error('暂不支持 HEIC 格式，请用拍照上传或先转为 JPG');
  }
  if (file.size > 25 * 1024 * 1024) {
    throw new Error('图片过大（超过25MB），请换一张或降低拍摄分辨率');
  }
  // 已足够小则直接上传，避免无谓重编码
  if (file.size <= ASSET_PHOTO_TARGET_BYTES && type.includes('jpeg')) {
    return file;
  }
  try {
    const compressed = await compressImageFile(file);
    if (compressed.size > ASSET_PHOTO_MAX_BYTES) {
      throw new Error('压缩后仍超过系统限制，请换一张更小的照片');
    }
    return compressed;
  } catch (err) {
    if (file.size <= ASSET_PHOTO_MAX_BYTES && type.startsWith('image/')) {
      return file;
    }
    throw err instanceof Error ? err : new Error('图片处理失败');
  }
}

export function movePhotoInList(list, fromId, toIndex) {
  const next = Array.isArray(list) ? [...list] : [];
  const fromIdx = next.findIndex((p) => p.id === fromId);
  if (fromIdx < 0 || toIndex < 0 || toIndex >= next.length) return next;
  const [item] = next.splice(fromIdx, 1);
  next.splice(toIndex, 0, item);
  return next;
}

export function setPhotoAsCover(list, photoId) {
  const next = Array.isArray(list) ? [...list] : [];
  const idx = next.findIndex((p) => p.id === photoId);
  if (idx <= 0) return next;
  const [item] = next.splice(idx, 1);
  next.unshift(item);
  return next;
}
